/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-10
 */

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines-per-function */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { DialogService } from 'nebula-ui-kit';
import {
  ConceptosFacturacionModal,
  ConceptosFacturacionService,
} from './conceptos-facturacion.service';
import { lastValueFrom, map, Observable } from 'rxjs';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';
import { TranslationService } from '@nebula-shared/data-access';
import { ordersByDelDialogo } from '../../../select-dialog/orden-dialogo';

@Injectable({
  providedIn: 'root',
})
export class ConceptosFacturacionModalService {
  readonly dialogService = inject(DialogService);
  readonly conceptosFacturacionService = inject(ConceptosFacturacionService);
  readonly translation = inject(TranslationService);
  protected t(key: string, params?: any): string {
    return this.translation.translate(key, params);
  }

  openSelector(
    title: string,
    currentValue?: string,
    soloActivos = true,
  ): Observable<ConceptosFacturacionModal[] | null> {
    const columns = [
      {
        key: 'codigo',
        label: this.t('COLUMNS.CODE'),
        sortable: true,
        width: '30%',
      },
      {
        key: 'nombreConcepto',
        label: this.t('COLUMNS.NAME'),
        sortable: true,
        width: '40%',
      },
      {
        key: 'tipoConcepto',
        label: this.t('INVOICING.CONCEPTS.FORM.CONCEPT_TYPE'),
        sortable: true,
        width: '15%',
      },
      {
        key: 'estadoConcepto',
        label: this.t('COLUMNS.STATUS'),
        sortable: true,
        width: '15%',
        displayFn: (item: any) => {
          return item.estadoConcepto || 'N/A';
        },
      },
    ];

    const loadDataFn = async (params: any): Promise<{ data: any[]; total: number }> => {
      try {
        const page = params.page - 1;
        const size = params.limit;
        const searchTerm = params.search || '';

        const response = await lastValueFrom(
          this.conceptosFacturacionService.getConceptosFacturacionPaginados(
            page,
            size,
            searchTerm,
            soloActivos,
            ordersByDelDialogo(params),
          ),
        );

        return {
          data: response.content,
          total: response.totalElements,
        };
      } catch (error) {
        return { data: [], total: 0 };
      }
    };

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: columns,
        multiSelect: true,
        searchPlaceholder: this.t('INVOICING.CONCEPTS.ACCOUNTING_SETUP.SEARCH.PLACEHOLDER'),
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        selectedItems: currentValue ? [{ id: currentValue }] : [],
        displayFn: (item: any) => {
          const codigo = item.codigo || '';
          const nombre = item.nombreConcepto || item.nombre || '';
          const tipo = item.tipoConcepto || '';
          const estado = item.estadoConcepto || '';

          let displayText = `${codigo} - ${this.truncateText(nombre, 30)}`;
          if (tipo) {
            displayText += ` (${tipo})`;
          }
          if (estado) {
            displayText += ` - ${estado}`;
          }
          return displayText;
        },
      },
      size: 'lg',
      width: '800px',
    });

    return dialogRef.afterClosed().pipe(
      map((selected: ConceptosFacturacionModal[]) => {
        return selected && selected.length > 0 ? selected : null;
      }),
    );
  }

  truncateText(text: string, maxLength = 20): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }
}
